import { useState, useEffect } from 'react';
import { useBack, buzz } from '../hooks';
import api from '../api';

const DARK  = '#0f1117';
const CARD  = '#1e2329';
const BORD  = '#2a3040';
const MUTED = '#6b7280';
const TEAL  = '#14b8a6';

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '44px', height: '24px', borderRadius: '12px', flexShrink: 0,
        background: on ? TEAL : '#374151',
        cursor: 'pointer', position: 'relative',
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: '3px',
        left: on ? '23px' : '3px',
        width: '18px', height: '18px', borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
      }} />
    </div>
  );
}

const VIEW_OPTIONS = [
  { key: 'day',   label: 'Day' },
  { key: '1w',    label: '1w' },
  { key: '2w',    label: '2w' },
  { key: '3w',    label: '3w' },
  { key: '4w',    label: '4w' },
  { key: 'arrow', label: '→' },
  { key: 'month', label: 'Month' },
];

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pretty = (d) => { const x = new Date(d + 'T12:00:00Z'); return `${WEEKDAY[x.getUTCDay()]} ${x.getUTCDate()} ${MONTH[x.getUTCMonth()]}`; };

// The frozen zone: how many working days ahead the floor may already be set up
// for. Re-flow never moves a run starting inside it, and a drag into or out of
// it asks first. Company-wide and kept on the server -- unlike the device
// preferences on this page -- and every change is recorded. Also settable
// where a shift window is set up, since that is where it bites.
function FrozenZoneSetting() {
  const [fz, setFz] = useState(null);       // { working_days, frozen_until, first_free, today }
  const [draft, setDraft] = useState(2);
  const [state, setState] = useState(null);  // null | 'saving' | 'saved' | error text

  useEffect(() => {
    api.get('/api/v1/reflow/frozen-zone')
      .then(r => { setFz(r.data); setDraft(r.data.working_days ?? 2); })
      .catch(() => setState('The frozen zone could not be read just now.'));
  }, []);

  function save() {
    setState('saving');
    api.put('/api/v1/reflow/frozen-zone', { working_days: draft })
      .then(r => { setFz(r.data); setState('saved'); })
      .catch(e => setState(e.response?.data?.error || 'Could not change the frozen zone'));
  }

  const changed = fz && draft !== fz.working_days;
  const btn = { width: '28px', height: '28px', borderRadius: '6px', background: '#131920', border: `1px solid ${BORD}`, color: '#f0f2f5', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' };
  return (
    <div style={{ padding: '16px 18px', background: CARD }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>Frozen zone</div>
          <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.5 }}>
            Working days ahead the floor may already be set up for. Re-flow leaves runs there alone, and a drag into them asks first.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button style={btn} onClick={() => { setDraft(d => Math.max(0, d - 1)); setState(null); }}>−</button>
          <span style={{ minWidth: '54px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: TEAL }}>{draft} day{draft !== 1 ? 's' : ''}</span>
          <button style={btn} onClick={() => { setDraft(d => Math.min(20, d + 1)); setState(null); }}>+</button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '12px', borderTop: `1px solid ${BORD}`, paddingTop: '10px' }}>
        <div style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.5 }}>
          {!fz ? (typeof state === 'string' && state !== 'saving' && state !== 'saved' ? state : 'Reading…')
            : fz.working_days === 0 ? 'No frozen zone: every day from tomorrow may move.'
            : <>Now protects up to <b style={{ color: '#9ca3af' }}>{pretty(fz.frozen_until)}</b>; the first day that may move is {pretty(fz.first_free)}.</>}
          {state === 'saved' && <span style={{ color: TEAL }}> · Saved</span>}
          {state && !['saving', 'saved'].includes(state) && fz && <span style={{ color: '#fca5a5' }}> · {state}</span>}
        </div>
        {changed && (
          <button onClick={save} disabled={state === 'saving'}
            style={{ padding: '7px 16px', fontSize: '13px', fontWeight: '600', border: `1px solid ${TEAL}`, borderRadius: '8px', background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
            {state === 'saving' ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

// The window (the director, 8 Oct 2026): while an order has time, base
// material and the colour order decide the order on a machine; in its last
// working days before the cut-off, the delivery date decides, and a product's
// parts that share a machine are moulded in their build steps. Kept on the
// server; changes recorded.
function BuildOrderWindowSetting() {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(10);
  const [state, setState] = useState(null);
  useEffect(() => {
    api.get('/api/v1/products/build-order/window')
      .then(r => { setSaved(r.data.working_days); setDraft(r.data.working_days); })
      .catch(() => setState('The build-order window could not be read just now.'));
  }, []);
  function save() {
    setState('saving');
    api.put('/api/v1/products/build-order/window', { working_days: draft })
      .then(r => { setSaved(r.data.working_days); setState('saved'); })
      .catch(e => setState(e.response?.data?.error || 'Could not change the build-order window'));
  }
  const changed = saved != null && draft !== saved;
  const btn = { width: '28px', height: '28px', borderRadius: '6px', background: '#131920', border: `1px solid ${BORD}`, color: '#f0f2f5', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' };
  return (
    <div style={{ padding: '16px 18px', background: CARD, borderTop: `1px solid ${BORD}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>When the delivery date takes over</div>
          <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.5 }}>
            Working days before an order's cut-off. Before them, base material and the colour order (below) decide the order on a machine. Within them, the delivery date decides, and a product's parts that share a machine are moulded in their build steps (set on each product).
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button style={btn} onClick={() => { setDraft(d => Math.max(0, d - 1)); setState(null); }}>−</button>
          <span style={{ minWidth: '54px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: TEAL }}>{draft} day{draft !== 1 ? 's' : ''}</span>
          <button style={btn} onClick={() => { setDraft(d => Math.min(40, d + 1)); setState(null); }}>+</button>
        </div>
      </div>
      {(changed || state) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '12px', borderTop: `1px solid ${BORD}`, paddingTop: '10px' }}>
          <div style={{ fontSize: '11.5px', color: state && !['saving', 'saved'].includes(state) ? '#fca5a5' : TEAL }}>
            {state === 'saved' ? 'Saved' : state === 'saving' ? '' : state}
          </div>
          {changed && (
            <button onClick={save} disabled={state === 'saving'}
              style={{ padding: '7px 16px', fontSize: '13px', fontWeight: '600', border: `1px solid ${TEAL}`, borderRadius: '8px', background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
              {state === 'saving' ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The colour order (the director, 8 Oct 2026): the order masterbatches run in
// on a machine, lightest first. Kept per masterbatch, because the app's colour
// names are too coarse ("Red" is both a light and a full red). Parts with no
// masterbatch whose own colour is Transparent or Natural always go first; a
// part with no masterbatch and any other colour is listed as not known --
// the masterbatch may have been missed. Kept on the server; changes recorded.
function ColourOrderSetting() {
  const [data, setData] = useState(null);    // { saved, clear, order, unknown }
  const [draft, setDraft] = useState([]);    // the masterbatches, in order
  const [state, setState] = useState(null);  // null | 'saving' | 'saved' | error text
  const [showUnknown, setShowUnknown] = useState(false);
  // How a drag that makes a purge is told (the conflict engine's colour_purge
  // rule): asked before it lands, or only marked on the board.
  const [purgeAsk, setPurgeAsk] = useState(null);     // true | false | null (not read yet)
  const [purgeState, setPurgeState] = useState(null); // null | 'saving' | 'saved' | error text
  useEffect(() => {
    api.get('/api/v1/materials/colour-order')
      .then(r => { setData(r.data); setDraft(r.data.order); })
      .catch(() => setState('The colour order could not be read just now.'));
    api.get('/api/v1/conflicts/rules')
      .then(r => { const rule = (r.data || []).find(x => x.id === 'colour_purge'); if (rule) setPurgeAsk(rule.enabled && rule.severity !== 'note'); })
      .catch(() => {});
  }, []);
  function setAsk(ask) {
    if (ask === purgeAsk) return;
    setPurgeState('saving');
    api.put('/api/v1/conflicts/rules/colour_purge', { enabled: true, severity: ask ? 'warning' : 'note' })
      .then(() => { setPurgeAsk(ask); setPurgeState('saved'); })
      .catch(e => setPurgeState(e.response?.data?.error || 'Could not change how a purge is told'));
  }
  function move(i, dir) {
    setDraft(d => {
      const j = i + dir;
      if (j < 0 || j >= d.length) return d;
      const n = d.slice(); [n[i], n[j]] = [n[j], n[i]]; return n;
    });
    setState(null);
  }
  function save() {
    setState('saving');
    api.put('/api/v1/materials/colour-order', { order: draft.map(m => m.id) })
      .then(() => {
        const mine = draft.map(m => ({ ...m, placed_by: 'you' }));
        setDraft(mine); setData(d => ({ ...d, saved: true, order: mine })); setState('saved');
      })
      .catch(e => setState(e.response?.data?.error || 'Could not save the colour order'));
  }
  const changed = !!data && (!data.saved || draft.map(m => m.id).join() !== data.order.map(m => m.id).join());
  const partsText = ps => (!ps.length ? 'No parts use it yet'
    : ps.length <= 3 ? ps.join(', ') : `${ps.slice(0, 3).join(', ')} and ${ps.length - 3} more`);
  const row = { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: DARK, border: `1px solid ${BORD}`, borderRadius: '8px' };
  const arrow = off => ({ background: 'none', border: 'none', color: off ? BORD : '#9ca3af', cursor: off ? 'default' : 'pointer', fontSize: '10px', lineHeight: 1, padding: '1px 3px' });
  const swatch = hex => ({ width: '14px', height: '14px', borderRadius: '50%', background: hex || MUTED, border: '1px solid rgba(255,255,255,0.18)', flexShrink: 0 });
  return (
    <div style={{ padding: '16px 18px', background: CARD, borderTop: `1px solid ${BORD}` }}>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>Colour order</div>
      <div style={{ fontSize: '12px', color: MUTED, lineHeight: 1.5, marginBottom: '12px' }}>
        The order masterbatches run in on a machine, lightest first (▲ ▼ to arrange). Base material decides before colour, and a machine carries on from the colour already on it. A run close to its cut-off goes by its delivery date instead.
      </div>
      {data && !data.saved && (
        <div style={{ fontSize: '12px', color: '#fbbf24', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', lineHeight: 1.5 }}>
          Not saved yet: this order is the app's guess by lightness. Arrange it and save, and the planner uses yours.
        </div>
      )}
      {!data && !state && <div style={{ fontSize: '12px', color: MUTED }}>Loading…</div>}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ ...row, opacity: 0.85 }}>
            <span style={{ width: '22px', fontSize: '10px', color: MUTED, textAlign: 'center', flexShrink: 0 }}>first</span>
            <span style={{ ...swatch('#e2e8f0'), background: 'linear-gradient(135deg, #e2e8f0 50%, #f5f0e8 50%)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '600' }}>Transparent and natural</div>
              <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.clear.join(', ')}>
                No masterbatch, and the part's colour says so · {partsText(data.clear)}
              </div>
            </div>
          </div>
          {draft.map((m, i) => (
            <div key={m.id} style={row}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(20,184,166,0.14)', color: TEAL, fontSize: '11px', fontWeight: 800, display: 'grid', placeItems: 'center' }}>{i + 1}</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${m.name} earlier`} style={arrow(i === 0)}>▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === draft.length - 1} aria-label={`Move ${m.name} later`} style={arrow(i === draft.length - 1)}>▼</button>
                </div>
              </div>
              <span style={swatch(m.hex)} title={m.colour || 'No colour set'} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {m.name}
                  {data.saved && m.placed_by === 'lightness' && (
                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#fbbf24', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '4px', padding: '1px 5px' }}>new · placed by lightness</span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.parts.join(', ')}>{partsText(m.parts)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {purgeAsk !== null && (
        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.5, flex: '1 1 200px' }}>
            When a drag on the board makes a purge
            {purgeState && purgeState !== 'saving' && (
              <span style={{ marginLeft: '8px', color: purgeState === 'saved' ? TEAL : '#fca5a5' }}>{purgeState === 'saved' ? 'Saved' : purgeState}</span>
            )}
          </div>
          <div style={{ display: 'flex', border: `1px solid ${BORD}`, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
            {[[true, 'Ask before it lands'], [false, 'Only mark it']].map(([v, label]) => (
              <button key={label} onClick={() => setAsk(v)} disabled={purgeState === 'saving'}
                style={{ padding: '6px 11px', fontSize: '12px', fontWeight: purgeAsk === v ? 700 : 500, border: 'none', cursor: 'pointer', fontFamily: 'system-ui',
                  background: purgeAsk === v ? 'rgba(20,184,166,0.15)' : 'transparent', color: purgeAsk === v ? TEAL : '#9ca3af' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {data && data.unknown.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <button onClick={() => setShowUnknown(v => !v)}
            style={{ background: 'none', border: 'none', color: '#fbbf24', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className={`fa-solid fa-chevron-${showUnknown ? 'down' : 'right'}`} style={{ fontSize: '10px' }} />
            {data.unknown.length} part{data.unknown.length === 1 ? ' has' : 's have'} no known colour, so base material and date place {data.unknown.length === 1 ? 'it' : 'them'}
          </button>
          {showUnknown && (
            <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px', color: '#9ca3af', lineHeight: 1.55 }}>
              {data.unknown.map(u => <li key={u.part_id}>{u.says}</li>)}
            </ul>
          )}
        </div>
      )}
      {(changed || state) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '12px', borderTop: `1px solid ${BORD}`, paddingTop: '10px' }}>
          <div style={{ fontSize: '11.5px', color: state && !['saving', 'saved'].includes(state) ? '#fca5a5' : TEAL }}>
            {state === 'saved' ? 'Saved' : state === 'saving' ? '' : state}
          </div>
          {changed && (
            <button onClick={save} disabled={state === 'saving'}
              style={{ padding: '7px 16px', fontSize: '13px', fontWeight: '600', border: `1px solid ${TEAL}`, borderRadius: '8px', background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
              {state === 'saving' ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const goBack = useBack('/admin');
  const [hapticsOn, setHapticsOn] = useState(
    () => localStorage.getItem('haptics_enabled') === 'true'
  );
  const [defaultView, setDefaultView] = useState(
    () => localStorage.getItem('schedule_default_view') || 'arrow'
  );

  // --- Schedule prefs ---
  const [refreshInterval, setRefreshInterval] = useState(
    () => localStorage.getItem('schedule_refresh_interval') || '0'
  );
  const [defaultFilter, setDefaultFilter] = useState(
    () => localStorage.getItem('schedule_default_filter') || ''
  );

  // --- Display prefs ---
  const [showCompleted, setShowCompleted] = useState(
    () => localStorage.getItem('schedule_show_completed') !== 'false'
  );
  const [compactDefault, setCompactDefault] = useState(
    () => localStorage.getItem('schedule_compact_default') === 'true'
  );
  const [urgencyRed, setUrgencyRed] = useState(
    () => parseInt(localStorage.getItem('schedule_urgency_red') || '2', 10)
  );
  const [urgencyAmber, setUrgencyAmber] = useState(
    () => parseInt(localStorage.getItem('schedule_urgency_amber') || '7', 10)
  );

  function toggleHaptics() {
    const next = !hapticsOn;
    localStorage.setItem('haptics_enabled', String(next));
    setHapticsOn(next);
    if (next) buzz(40); // immediate confirmation buzz when enabling
  }

  function testBuzz() {
    buzz(60);
  }

  function pickDefaultView(key) {
    localStorage.setItem('schedule_default_view', key);
    setDefaultView(key);
  }

  function pickRefresh(val) {
    localStorage.setItem('schedule_refresh_interval', val);
    setRefreshInterval(val);
  }

  function saveFilter(val) {
    localStorage.setItem('schedule_default_filter', val);
    setDefaultFilter(val);
  }

  function toggleShowCompleted() {
    const next = !showCompleted;
    localStorage.setItem('schedule_show_completed', String(next));
    setShowCompleted(next);
  }

  function toggleCompactDefault() {
    const next = !compactDefault;
    localStorage.setItem('schedule_compact_default', String(next));
    setCompactDefault(next);
  }

  function setUrgencyRedVal(n) {
    const v = Math.max(0, Math.min(30, n));
    localStorage.setItem('schedule_urgency_red', String(v));
    setUrgencyRed(v);
  }

  function setUrgencyAmberVal(n) {
    const v = Math.max(0, Math.min(30, n));
    localStorage.setItem('schedule_urgency_amber', String(v));
    setUrgencyAmber(v);
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'system-ui, sans-serif', color: '#f0f2f5', background: DARK, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>

        {/* Header */}
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '13px', padding: '0', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '11px' }} />
          Back
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>Settings</h1>
        <p style={{ color: MUTED, fontSize: '14px', marginBottom: '28px' }}>App preferences and controls</p>

        {/* Planning section: company-wide, on the server */}
        <div style={{ border: `1px solid ${BORD}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <i className="fa-solid fa-snowflake" style={{ marginRight: '6px' }} />
              Planning · for everyone
            </span>
          </div>
          <FrozenZoneSetting />
          <BuildOrderWindowSetting />
          <ColourOrderSetting />
        </div>

        {/* Device section */}
        <div style={{ border: `1px solid ${BORD}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORD}`, background: `${CARD}` }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <i className="fa-solid fa-mobile-screen" style={{ marginRight: '6px' }} />
              Device
            </span>
          </div>

          {/* Haptic feedback row */}
          <div style={{ padding: '16px 18px', background: CARD }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>Haptic Feedback</div>
                <div style={{ fontSize: '12px', color: MUTED }}>
                  Subtle vibration on taps and interactions
                </div>
              </div>
              <Toggle on={hapticsOn} onChange={toggleHaptics} />
            </div>
            <div style={{ fontSize: '11px', color: '#4b5563', lineHeight: '1.5', borderTop: `1px solid ${BORD}`, paddingTop: '10px', marginTop: '4px' }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: '5px', color: '#374151' }} />
              Supported on Android (Chrome, Firefox). Not available on iOS Safari or desktop browsers — the toggle has no effect on those devices.
            </div>
            {hapticsOn && (
              <button
                onClick={testBuzz}
                style={{ marginTop: '12px', background: 'rgba(20,184,166,0.08)', border: `1px solid ${TEAL}44`, borderRadius: '8px', color: TEAL, fontSize: '12px', fontWeight: '600', padding: '7px 16px', cursor: 'pointer', fontFamily: 'system-ui' }}
              >
                <i className="fa-solid fa-hand-pointer" style={{ marginRight: '6px' }} />
                Test vibration
              </button>
            )}
          </div>
        </div>

        {/* Schedule section */}
        <div style={{ border: `1px solid ${BORD}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <i className="fa-solid fa-calendar-week" style={{ marginRight: '6px' }} />
              Schedule
            </span>
          </div>

          {/* Default view */}
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Default View</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px' }}>Which view opens when you navigate to Schedule</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {VIEW_OPTIONS.map(({ key, label }) => (
                <button key={key} onClick={() => pickDefaultView(key)} style={{ padding: '7px 14px', fontSize: '13px', fontWeight: '600', border: `1px solid ${defaultView === key ? TEAL : BORD}`, borderRadius: '8px', background: defaultView === key ? 'rgba(20,184,166,0.12)' : '#131920', color: defaultView === key ? TEAL : '#9ca3af', cursor: 'pointer', fontFamily: 'system-ui', transition: 'all 0.12s' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Auto-refresh */}
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Auto-refresh</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px' }}>Reload schedule data automatically while you have it open</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[['0', 'Off'], ['30', '30s'], ['60', '1 min'], ['300', '5 min']].map(([val, label]) => (
                <button key={val} onClick={() => pickRefresh(val)} style={{ padding: '7px 14px', fontSize: '13px', fontWeight: '600', border: `1px solid ${refreshInterval === val ? TEAL : BORD}`, borderRadius: '8px', background: refreshInterval === val ? 'rgba(20,184,166,0.12)' : '#131920', color: refreshInterval === val ? TEAL : '#9ca3af', cursor: 'pointer', fontFamily: 'system-ui', transition: 'all 0.12s' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Default filter */}
          <div style={{ padding: '16px 18px', background: CARD }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Default Filter</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '12px' }}>Pre-fills the schedule search box on every visit</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                value={defaultFilter}
                onChange={e => setDefaultFilter(e.target.value)}
                placeholder="e.g. Stülper, machine name…"
                style={{ flex: 1, background: '#131920', border: `1px solid ${BORD}`, borderRadius: '8px', color: '#f0f2f5', padding: '8px 12px', fontSize: '13px', outline: 'none', fontFamily: 'system-ui' }}
              />
              <button onClick={() => saveFilter(defaultFilter)} style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', border: `1px solid ${TEAL}`, borderRadius: '8px', background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' }}>
                Save
              </button>
              {defaultFilter && (
                <button onClick={() => saveFilter('')} style={{ padding: '8px 12px', fontSize: '12px', fontWeight: '600', border: `1px solid ${BORD}`, borderRadius: '8px', background: 'transparent', color: MUTED, cursor: 'pointer', fontFamily: 'system-ui' }}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Display section */}
        <div style={{ border: `1px solid ${BORD}`, borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <i className="fa-solid fa-eye" style={{ marginRight: '6px' }} />
              Display
            </span>
          </div>

          {/* Show completed */}
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>Show completed jobs</div>
                <div style={{ fontSize: '12px', color: MUTED }}>Hide fully-completed plan cards from the schedule grid</div>
              </div>
              <Toggle on={showCompleted} onChange={toggleShowCompleted} />
            </div>
          </div>

          {/* Compact rows */}
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${BORD}`, background: CARD }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px' }}>Compact machine rows</div>
                <div style={{ fontSize: '12px', color: MUTED }}>Start with all machine rows collapsed to a thin bar</div>
              </div>
              <Toggle on={compactDefault} onChange={toggleCompactDefault} />
            </div>
          </div>

          {/* Urgency thresholds */}
          <div style={{ padding: '16px 18px', background: CARD }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Urgency warning thresholds</div>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>
              How many calendar days before the delivery Friday a plan triggers amber or red tinting
            </div>
            {[
              { label: 'Amber warning', color: '#f59e0b', val: urgencyAmber, set: setUrgencyAmberVal },
              { label: 'Red warning',   color: '#ef4444', val: urgencyRed,   set: setUrgencyRedVal },
            ].map(({ label, color, val, set }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: '600' }}>{label}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={() => set(val - 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#131920', border: `1px solid ${BORD}`, color: '#f0f2f5', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>−</button>
                  <span style={{ minWidth: '44px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: color }}>{val} day{val !== 1 ? 's' : ''}</span>
                  <button onClick={() => set(val + 1)} style={{ width: '28px', height: '28px', borderRadius: '6px', background: '#131920', border: `1px solid ${BORD}`, color: '#f0f2f5', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>+</button>
                </div>
              </div>
            ))}
            <div style={{ fontSize: '11px', color: '#4b5563', lineHeight: '1.5', borderTop: `1px solid ${BORD}`, paddingTop: '10px' }}>
              <i className="fa-solid fa-circle-info" style={{ marginRight: '5px', color: '#374151' }} />
              Default: amber at 7 days, red at 2 days. Amber threshold should be higher than red.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
